import { useEffect, useMemo, useRef, useState } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";
import { RGBELoader } from "three/examples/jsm/loaders/RGBELoader.js";
import { EffectComposer } from "three/examples/jsm/postprocessing/EffectComposer.js";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { SMAAPass } from "three/examples/jsm/postprocessing/SMAAPass.js";
import sparcliLogo from "../assets/img/logo-neg.png";
import "./Model3DDemo.css";

const DEFAULT_MODEL = "/assets/3d/707169d2-48d7-4446-9769-8892ffcd042a.glb";
const DEFAULT_HDR_FALLBACK = "/assets/3d/studio_small_03_2k.hdr";
const MODELS_MANIFEST = "/assets/3d/models.json";
const HDRS_MANIFEST = "/hdr/hdrs.json";

type MaterialPreset = "crystal" | "opaque";
type SelectOption = { value: string; label: string };

function getRequestedParam(name: string) {
    return new URLSearchParams(window.location.search).get(name);
}

function basename(path: string) {
    return path.split("/").pop() ?? path;
}

function pickByQuery(query: string | null, options: SelectOption[], fallback: string) {
    if (!query) return fallback;
    const exact = options.find((opt) => opt.value === query);
    if (exact) return exact.value;
    const byName = options.find((opt) => basename(opt.value) === basename(query));
    return byName?.value ?? fallback;
}

function disposeMaterial(mat: THREE.Material | THREE.Material[]) {
    if (Array.isArray(mat)) {
        mat.forEach((m) => m.dispose());
        return;
    }
    mat.dispose();
}

export function Model3DDemo() {
    const hostRef = useRef<HTMLDivElement>(null);
    const controlsRef = useRef<OrbitControls | null>(null);
    const cameraRef = useRef<THREE.PerspectiveCamera | null>(null);

    const [models, setModels] = useState<string[]>([]);
    const [selectedModel, setSelectedModel] = useState("");
    const [hdrOptions, setHdrOptions] = useState<SelectOption[]>([]);
    const [selectedHdr, setSelectedHdr] = useState("");
    const [materialPreset, setMaterialPreset] = useState<MaterialPreset>("crystal");
    const [error, setError] = useState<string | null>(null);

    const lockedModelUrl = useMemo(
        () => getRequestedParam("modelUrl") || getRequestedParam("url") || getRequestedParam("file"),
        []
    );
    const isModelLocked = !!lockedModelUrl;
    const modelPath = isModelLocked
        ? (lockedModelUrl as string)
        : (selectedModel ? `/assets/3d/${selectedModel}` : DEFAULT_MODEL);

    useEffect(() => {
        let cancelled = false;
        const requestedModel = getRequestedParam("model");
        const requestedHdr = getRequestedParam("hdr");
        const requestedMaterial = getRequestedParam("material");

        if (requestedMaterial === "opaque" || requestedMaterial === "crystal") {
            setMaterialPreset(requestedMaterial);
        }

        const loadSelectors = async () => {
            try {
                const [modelsResponse, hdrResponse] = await Promise.all([
                    fetch(MODELS_MANIFEST, { cache: "no-store" }),
                    fetch(HDRS_MANIFEST, { cache: "no-store" }),
                ]);

                if (cancelled) return;

                let modelList: string[] = [];
                if (modelsResponse.ok) {
                    const modelsJson = await modelsResponse.json();
                    const modelsRaw = Array.isArray(modelsJson?.models) ? modelsJson.models : [];
                    modelList = modelsRaw
                        .filter((name: unknown) => typeof name === "string" && name.toLowerCase().endsWith(".glb"))
                        .sort((a: string, b: string) => a.localeCompare(b));
                }

                const uniqueModels = Array.from(new Set(modelList));
                const fallbackModel = basename(DEFAULT_MODEL);
                const finalModels = uniqueModels.length > 0 ? uniqueModels : [fallbackModel];
                setModels(finalModels);
                if (!isModelLocked) {
                    setSelectedModel(
                        requestedModel && finalModels.includes(requestedModel) ? requestedModel : finalModels[0]
                    );
                }

                let hdrs: string[] = [];
                if (hdrResponse.ok) {
                    const hdrJson = await hdrResponse.json();
                    const hdrRaw = Array.isArray(hdrJson?.hdrs) ? hdrJson.hdrs : [];
                    hdrs = hdrRaw
                        .filter((name: unknown) => typeof name === "string" && (name.toLowerCase().endsWith(".hdr") || name.toLowerCase().endsWith(".exr")))
                        .sort((a: string, b: string) => a.localeCompare(b));
                }

                const options = hdrs.length > 0
                    ? hdrs.map((entry) => ({ value: entry.startsWith("/") ? entry : `/hdr/${entry}`, label: basename(entry) }))
                    : [{ value: DEFAULT_HDR_FALLBACK, label: `${basename(DEFAULT_HDR_FALLBACK)} (fallback)` }];
                setHdrOptions(options);
                setSelectedHdr(pickByQuery(requestedHdr, options, options[0].value));
            } catch {
                if (cancelled) return;

                const fallbackModel = basename(DEFAULT_MODEL);
                setModels([fallbackModel]);
                if (!isModelLocked) {
                    setSelectedModel(requestedModel || fallbackModel);
                }

                const fallbackOption = { value: DEFAULT_HDR_FALLBACK, label: `${basename(DEFAULT_HDR_FALLBACK)} (fallback)` };
                setHdrOptions([fallbackOption]);
                setSelectedHdr(fallbackOption.value);
            }
        };

        loadSelectors();
        return () => {
            cancelled = true;
        };
    }, [isModelLocked]);

    useEffect(() => {
        if (!selectedHdr) return;
        const url = new URL(window.location.href);
        if (isModelLocked && lockedModelUrl) {
            url.searchParams.set("modelUrl", lockedModelUrl);
            url.searchParams.delete("model");
        } else if (selectedModel) {
            url.searchParams.set("model", selectedModel);
            url.searchParams.delete("modelUrl");
        }
        url.searchParams.set("hdr", basename(selectedHdr));
        url.searchParams.set("material", materialPreset);
        window.history.replaceState({}, "", url.toString());
    }, [selectedModel, selectedHdr, materialPreset, isModelLocked, lockedModelUrl]);

    useEffect(() => {
        if (!selectedHdr) return;
        if (!isModelLocked && !selectedModel) return;
        const host = hostRef.current;
        if (!host) return;
        setError(null);

        const scene = new THREE.Scene();
        const camera = new THREE.PerspectiveCamera(60, 1, 0.001, 2000);
        camera.position.set(1.8, 1.2, 2.4);

        const renderer = new THREE.WebGLRenderer({ antialias: false, alpha: false });
        renderer.outputColorSpace = THREE.SRGBColorSpace;
        renderer.toneMapping = THREE.NoToneMapping;
        renderer.toneMappingExposure = 1.0;
        renderer.useLegacyLights = false;
        // Kept for compatibility with setups expecting this toggle.
        (renderer as any).physicallyCorrectLights = true;
        host.appendChild(renderer.domElement);

        const composer = new EffectComposer(renderer);
        const renderPass = new RenderPass(scene, camera);
        composer.addPass(renderPass);
        const smaaPass = new SMAAPass(
            Math.max(host.clientWidth * renderer.getPixelRatio(), 1),
            Math.max(host.clientHeight * renderer.getPixelRatio(), 1)
        );
        composer.addPass(smaaPass);

        const controls = new OrbitControls(camera, renderer.domElement);
        controlsRef.current = controls;
        cameraRef.current = camera;
        controls.enableDamping = true;
        controls.dampingFactor = 0.05;
        controls.target.set(0, 0, 0);
        controls.update();

        scene.add(new THREE.AmbientLight(0xffffff, 1.1));
        const keyLight = new THREE.DirectionalLight(0xffffff, 2.1);
        keyLight.position.set(3, 4.5, 2);
        scene.add(keyLight);
        const fillLight = new THREE.DirectionalLight(0xcde6ff, 1.35);
        fillLight.position.set(-2.2, 2, -1.7);
        scene.add(fillLight);
        const rimLight = new THREE.DirectionalLight(0xffffff, 1.05);
        rimLight.position.set(-1, 1.6, 2.5);
        scene.add(rimLight);

        const pmrem = new THREE.PMREMGenerator(renderer);
        pmrem.compileEquirectangularShader();
        let hdrTexture: THREE.Texture | null = null;
        let hdrEnvironmentTarget: THREE.WebGLRenderTarget | null = null;
        let raf = 0;

        const resize = () => {
            const w = host.clientWidth;
            const h = host.clientHeight;
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
            renderer.setSize(w, h);
            composer.setSize(w, h);
            if (typeof (smaaPass as any).setSize === "function") {
                (smaaPass as any).setSize(
                    Math.max(w * renderer.getPixelRatio(), 1),
                    Math.max(h * renderer.getPixelRatio(), 1)
                );
            }
        };
        resize();
        const ro = new ResizeObserver(resize);
        ro.observe(host);

        new RGBELoader().load(
            selectedHdr,
            (tex) => {
                tex.mapping = THREE.EquirectangularReflectionMapping;
                hdrTexture = tex;
                scene.background = tex;
                scene.backgroundIntensity = 1.85;
                hdrEnvironmentTarget = pmrem.fromEquirectangular(tex);
                scene.environment = hdrEnvironmentTarget.texture;
            },
            undefined,
            () => {
                scene.background = new THREE.Color(0xc8e7ff);
                scene.environment = null;
                setError(`HDR non caricata: ${selectedHdr}`);
            }
        );

        const opaqueMaterial = new THREE.MeshPhysicalMaterial({
            color: new THREE.Color("#e8ebef"),
            roughness: 0.33,
            metalness: 0.03,
            clearcoat: 0.36,
            clearcoatRoughness: 0.2,
            transmission: 0,
            transparent: false,
            envMapIntensity: 1.1,
            side: THREE.DoubleSide,
        });

        const crystalBaseMaterial = new THREE.MeshPhysicalMaterial({
            color: new THREE.Color("#ffffff"),
            transmission: 1,
            transparent: true,
            opacity: 1,
            ior: 1.5,
            roughness: 0.03,
            thickness: 0.2,
            envMapIntensity: 1.2,
            clearcoat: 1,
            clearcoatRoughness: 0.02,
            attenuationColor: new THREE.Color("#e8f5ff"),
            attenuationDistance: 1.0,
        });

        const loader = new GLTFLoader();
        loader.load(
            modelPath,
            (gltf) => {
                const root = gltf.scene;
                const meshes: THREE.Mesh[] = [];

                root.traverse((obj) => {
                    if (!(obj instanceof THREE.Mesh)) return;
                    meshes.push(obj);
                    obj.castShadow = false;
                    obj.receiveShadow = false;
                    if (obj.geometry) {
                        obj.geometry.computeVertexNormals();
                        obj.geometry.normalizeNormals();
                    }
                });

                if (materialPreset === "opaque") {
                    meshes.forEach((mesh) => {
                        mesh.material = opaqueMaterial.clone();
                    });
                } else {
                    meshes.forEach((outer) => {
                        const outerMat = crystalBaseMaterial.clone();
                        outerMat.side = THREE.FrontSide;
                        outerMat.depthWrite = false;
                        outerMat.depthTest = true;
                        outer.material = outerMat;

                        const inner = outer.clone();
                        const innerMat = crystalBaseMaterial.clone();
                        innerMat.side = THREE.BackSide;
                        innerMat.depthWrite = false;
                        innerMat.depthTest = true;
                        inner.material = innerMat;
                        inner.scale.multiplyScalar(0.995);
                        outer.parent?.add(inner);
                    });
                }

                const box = new THREE.Box3().setFromObject(root);
                const center = box.getCenter(new THREE.Vector3());
                root.position.sub(center);
                scene.add(root);

                const fitBox = new THREE.Box3().setFromObject(root);
                const fitSphere = fitBox.getBoundingSphere(new THREE.Sphere());
                const radius = Math.max(fitSphere.radius, 0.001);
                const fov = THREE.MathUtils.degToRad(camera.fov);
                const fitDistance = (radius / Math.sin(fov / 2)) * 1.25;
                camera.position.set(radius * 0.9, radius * 0.65, fitDistance);
                camera.near = Math.max(fitDistance / 600, 0.0008);
                camera.far = Math.max(fitDistance * 320, 200);
                camera.updateProjectionMatrix();
                controls.minDistance = Math.max(radius * 0.35, 0.08);
                controls.maxDistance = Math.max(fitDistance * 5, 8);
                controls.target.set(0, 0, 0);
                controls.update();
            },
            undefined,
            (err) => {
                setError(`Impossibile caricare il modello: ${modelPath}`);
                console.error(err);
            }
        );

        const animate = () => {
            raf = requestAnimationFrame(animate);
            controls.update();
            composer.render();
        };
        animate();

        return () => {
            cancelAnimationFrame(raf);
            ro.disconnect();
            controls.dispose();
            pmrem.dispose();
            hdrTexture?.dispose();
            hdrEnvironmentTarget?.dispose();
            scene.traverse((obj) => {
                if (!(obj instanceof THREE.Mesh)) return;
                obj.geometry?.dispose();
                if (obj.material) disposeMaterial(obj.material);
            });
            opaqueMaterial.dispose();
            crystalBaseMaterial.dispose();
            composer.dispose();
            renderer.dispose();
            if (host.contains(renderer.domElement)) {
                host.removeChild(renderer.domElement);
            }
        };
    }, [modelPath, selectedModel, selectedHdr, materialPreset, isModelLocked]);

    const zoomBy = (factor: number) => {
        const camera = cameraRef.current;
        const controls = controlsRef.current;
        if (!camera || !controls) return;

        const toCamera = camera.position.clone().sub(controls.target);
        const dist = toCamera.length();
        const desired = THREE.MathUtils.clamp(dist * factor, 0.35, 16);
        camera.position.copy(controls.target.clone().add(toCamera.normalize().multiplyScalar(desired)));
        camera.updateProjectionMatrix();
        controls.update();
    };

    return (
        <section className="model3dDemoPage">
            <div className="model3dTopBar">
                <div className="model3dBrandPill">
                    <img className="model3dBrandLogo" src={sparcliLogo} alt="SparcliVision" />
                    <div className="model3dBrandTextWrap">
                        <div className="model3dBrandOverline">SparcliVision</div>
                        <div className="model3dTitle">Demo visualizzazione 3D</div>
                    </div>
                </div>
                <div className="model3dTopBarRight">
                    {!isModelLocked && (
                        <>
                            <label className="model3dSelectLabel" htmlFor="model-select">Bicchiere</label>
                            <select
                                id="model-select"
                                className="model3dSelect"
                                value={selectedModel}
                                onChange={(e) => setSelectedModel(e.target.value)}
                            >
                                {models.map((model) => (
                                    <option key={model} value={model}>{model}</option>
                                ))}
                            </select>
                        </>
                    )}
                    {isModelLocked && (
                        <>
                            <span className="model3dSelectLabel">File</span>
                            <span className="model3dFixedValue" title={lockedModelUrl ?? ""}>
                                {basename(lockedModelUrl ?? "")}
                            </span>
                        </>
                    )}
                    <label className="model3dSelectLabel" htmlFor="material-select">Materiale</label>
                    <select
                        id="material-select"
                        className="model3dSelect"
                        value={materialPreset}
                        onChange={(e) => setMaterialPreset(e.target.value as MaterialPreset)}
                    >
                        <option value="crystal">Cristallo</option>
                        <option value="opaque">Opaco</option>
                    </select>
                    <label className="model3dSelectLabel" htmlFor="hdr-select">HDR</label>
                    <select
                        id="hdr-select"
                        className="model3dSelect"
                        value={selectedHdr}
                        onChange={(e) => setSelectedHdr(e.target.value)}
                    >
                        {hdrOptions.map((hdr) => (
                            <option key={hdr.value} value={hdr.value}>{hdr.label}</option>
                        ))}
                    </select>
                </div>
            </div>
            <div className="model3dCanvasWrap" ref={hostRef} />
            <div className="model3dZoomControls">
                <button type="button" onClick={() => zoomBy(0.85)}>Zoom +</button>
                <button type="button" onClick={() => zoomBy(1.15)}>Zoom -</button>
            </div>
            {error && <div className="model3dError">{error}</div>}
        </section>
    );
}
